import type { CachedManifest, Manifest } from '@/types';
import { getAccessToken } from './supabase';
import { readCachedManifest, writeCachedManifest } from './db';

const API_URL = import.meta.env.VITE_API_URL;

class ApiError extends Error {
  constructor(public status: number, msg: string) { super(msg); }
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, 'not signed in');
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return fetch(`${API_URL}${path}`, { ...init, headers });
}

/**
 * Fetch the manifest with the three-layer cache contract:
 *   1. Read what we have from IndexedDB and return it instantly to the caller.
 *   2. Conditionally GET /manifest with `If-None-Match`. If 304, we're done.
 *   3. If 200, decode + persist + return the new copy.
 *
 * Callers receive a `{ cached, fresh }` pair — render `cached` immediately,
 * await `fresh` to revalidate. This is what makes the map feel instant on
 * cold start while still converging to truth in the background.
 */
export async function fetchManifest(): Promise<{
  cached: CachedManifest | null;
  fresh: Promise<CachedManifest | null>;
}> {
  const cached = await readCachedManifest();
  const fresh = (async (): Promise<CachedManifest | null> => {
    try {
      const headers: HeadersInit = {};
      if (cached?.etag) headers['If-None-Match'] = cached.etag;
      const res = await authedFetch('/manifest', { headers });

      if (res.status === 304) return cached;
      if (!res.ok) throw new ApiError(res.status, await res.text());

      const etag = res.headers.get('ETag') ?? '';
      const version = Number(res.headers.get('X-Manifest-Version') ?? '0');
      const payload = (await res.json()) as Manifest;

      const next: CachedManifest = { etag, version, payload, cached_at: Date.now() };
      await writeCachedManifest(next);
      return next;
    } catch (err) {
      // Offline or auth blip — keep what we have.
      console.warn('manifest revalidation failed', err);
      return cached;
    }
  })();
  return { cached, fresh };
}

export interface UpsertPinInput {
  idempotency_key: string;
  id?: string;
  lat: number;
  lng: number;
  title: string;
  place_name?: string;
  unlock_kind?: 'open' | 'time' | 'location' | 'condition';
  unlock_at?: string;
  unlock_lat?: number;
  unlock_lng?: number;
  unlock_radius_m?: number;
  unlock_condition?: Record<string, unknown>;
  hint?: string;
}

export async function upsertPin(input: UpsertPinInput): Promise<{ id: string }> {
  const res = await authedFetch('/pins', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

export interface AttachMemoryInput {
  idempotency_key: string;
  pin_id: string;
  kind: 'photo' | 'note' | 'audio' | 'letter';
  body?: string;
  media_sha256?: string;
  media_bytes?: number;
  mime?: string;
  lqip?: string;
  width?: number;
  height?: number;
  duration_ms?: number;
  taken_at?: string;
  unlock_at?: string;
}

export async function attachMemory(input: AttachMemoryInput): Promise<{ id: string }> {
  const res = await authedFetch('/memories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

export async function initMediaUpload(args: {
  sha256: string; bytes: number; mime: string;
}): Promise<{ upload_url: string; key: string; already_uploaded?: boolean }> {
  const res = await authedFetch('/media/init', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

export async function putMedia(uploadUrl: string, bytes: ArrayBuffer): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: bytes,
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

export function mediaUrl(sha256: string): string {
  return `${API_URL}/m/${sha256}`;
}

export interface CreateNoteInput {
  idempotency_key: string;
  to_user: string;
  body: string;
  due_at?: string;
  reply_to?: string;
}

export async function createNote(input: CreateNoteInput): Promise<{ id: string }> {
  const res = await authedFetch('/notes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

export interface CompleteNoteInput {
  idempotency_key: string;
  note_id: string;
  completed_pin_id?: string;
}

export async function completeNote(input: CompleteNoteInput): Promise<{ id: string }> {
  const res = await authedFetch(`/notes/${input.note_id}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      idempotency_key: input.idempotency_key,
      ...(input.completed_pin_id && { completed_pin_id: input.completed_pin_id }),
    }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

// ── delete operations ─────────────────────────────────────────────────────
// DELETE is naturally idempotent — calling twice with the same id is a no-op
// rather than a duplicate. So no idempotency_key wrapping required.

export async function deletePin(id: string): Promise<void> {
  const res = await authedFetch(`/pins/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

export async function deleteMemory(id: string): Promise<void> {
  const res = await authedFetch(`/memories/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

// ── bucket list ──────────────────────────────────────────────────────────
export interface AddBucketInput {
  idempotency_key: string;
  title: string;
  lat?: number;
  lng?: number;
  notes?: string;
}

export async function addBucket(input: AddBucketInput): Promise<{ id: string }> {
  const res = await authedFetch('/bucket', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

export async function completeBucket(args: {
  idempotency_key: string; bucket_id: string; pin_id: string;
}): Promise<{ id: string; completed_pin_id: string }> {
  const res = await authedFetch(`/bucket/${args.bucket_id}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idempotency_key: args.idempotency_key, pin_id: args.pin_id }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

export async function removeBucket(id: string): Promise<void> {
  const res = await authedFetch(`/bucket/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}
